<template>
    <div class="flex items-start max-w-full overflow-auto md:overflow-visible">
        <BtnRateDocument
            v-tooltip="{
                content: document.user_star_vote ? t('tooltip.rate_edit') : t('tooltip.rate'),
                disabled: document.user_is_uploader,
            }"
            :user-vote="document.user_star_vote"
            :avg-score="document.avg_star_score"
            :ratings-count="document.ratings_count"
            :document-id="document.id"
            class="mr-3"
            :size="screen.maxSM ? 'xxs' : btnSize"
            variant="outlined"
            :disabled="document.user_is_uploader"
            @update:user-vote="$store.commit('document/UPDATE_DOCUMENT_USER_STAR_VOTE', $event)"
            @update:avg-score="$store.commit('document/UPDATE_DOCUMENT_AVG_STAR_SCORE', $event)"
            @update:ratings-count="$store.commit('document/UPDATE_DOCUMENT_RATINGS_COUNT', $event)"
        />

        <BtnFollow
            v-if="screen.minMD && !document.user_is_uploader"
            v-tooltip="document.user_followed ? t('tooltip.unfollow_document') : t('tooltip.follow_document')"
            variant="outlined"
            class="protected-auth mr-3"
            :active="document.user_followed"
            :size="btnSize"
            @click="followToggle(false)"
        />

        <Btn
            v-tooltip="t('studyList.saveToList')"
            data-testid="save-to-studylist"
            variant="outlined"
            :left-icon="isDocumentInStudyList ? 'study-list-full' : 'study-list'"
            :size="btnSize"
            :active="isDocumentInStudyList"
            active-content-color="orange-300"
            :content-color="isDocumentInStudyList ? 'orange-300' : 'black'"
            :color="screen.minMD ? 'slate-300' : 'slate-400'"
            class="protected-auth mr-3"
            @click="studylistItemToggleDebounce"
        />

        <BtnMenu
            v-if="screen.maxMD"
            variant="outlined"
            :size="btnSize"
            left-icon="info"
            class="mr-3"
        >
            <template #menu>
                <DocumentShowDetails class="md:max-w-sm p-5" />
            </template>
        </BtnMenu>
        <BtnMenuShare
            class="mr-3"
            :share-title="
                t('share.title', {
                    name: document.filename,
                })
            "
            :share-url="shareURL"
            variant="outlined"
            :size="btnSize"
        />

        <BtnMenu
            v-if="user.userid"
            :tooltip-text="t('tooltip.more')"
            variant="outlined"
            :size="btnSize"
            left-icon="more"
            :options="options"
            menu-placement="bottom-end"
        />
    </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import { debounce } from 'lodash-es';
import BtnFollow from '@/components/BtnFollow';
import BtnRateDocument from '@/components/BtnRateDocument';
import stripWhiteSpaceSpecialCharacters from '@/helpers/stripWhiteSpaceSpecialCharacters';
import * as braze from '@braze/web-sdk';
import GtmMixin, { GtmEventName } from '@/mixins/gtmMixin';
import route from '@/router/route';
import BtnMenuShare from '@/components/BtnMenuShare';
import { notifySuccess } from '@/components/FlashMessages';
import { useTranslation } from 'i18next-vue';
import DocumentShowDetails from './DocumentShowDetails.vue';

export default {
    name: 'DocumentShowMenu',
    components: {
        BtnMenuShare,
        BtnRateDocument,
        DocumentShowDetails,
        BtnFollow,
    },
    extends: GtmMixin,
    setup() {
        const { t } = useTranslation('document');
        return { t };
    },
    data() {
        return {
            studylistItemToggleDebounce: null,
            getPdf: false,
        };
    },
    computed: {
        ...mapGetters({
            document: 'document/currentDocument',
            documentUniversity: 'document/currentDocumentUniversity',
            user: 'user/user',
            userUniversities: 'user/universities',
            inCourse: 'user/inCourse',
        }),
        ...mapGetters('ui', ['screen']),
        shareURL() {
            return route('document.show', {
                slug: this.document.slug,
                id: this.document.id,
                ref: this.user ? this.user.userid : null,
            });
        },
        isDocumentInStudyList() {
            return !!this.document.study_list_id;
        },
        shouldAutoJoinToCourse() {
            return (
                this.document.course &&
                this.documentUniversity &&
                !this.inCourse(this.document.course.id) &&
                this.userUniversities.includes(this.documentUniversity.id)
            );
        },
        options() {
            const options = [
                {
                    label: this.t('show.menu.edit'),
                    icon: 'edit',
                    onClick: this.openEditPopup,
                    visible: this.document.user_is_uploader,
                },
                {
                    label: this.document.user_followed
                        ? this.t('tooltip.unfollow_document')
                        : this.t('tooltip.follow_document'),
                    icon: this.document.user_followed ? 'bell-full' : 'bell',
                    onClick: this.followToggle,
                    visible: this.screen.maxSM && !this.document.user_is_uploader,
                },
                {
                    label: this.t('show.menu.report'),
                    icon: 'flag',
                    onClick: this.reportDocument,
                    visible: true,
                },
                {
                    label: this.t('show.menu.delete'),
                    icon: 'trash',
                    onClick: this.deleteDocument,
                    visible: this.document.user_is_uploader,
                },
            ];
            return options.filter((items) => items.visible);
        },
        btnSize() {
            return this.screen.maxSM ? 'xs' : 'sm';
        },
    },
    created() {
        this.studylistItemToggleDebounce = debounce(this.studylistItemToggle, 500);

        if (import.meta.env.MIX_BRAZE_SDK_ENABLED === 'true' && !this.document.user_is_uploader) {
            this.trackFileView();
        }
    },
    methods: {
        trackFileView() {
            setTimeout(
                () => {
                    braze.logCustomEvent('FILE-VIEWED', {
                        filename: this.document.filename,
                        course_name: this.document.course?.name,
                    });
                },
                1000 * import.meta.env.MIX_BRAZE_DOCUMENT_VIEW_TIME_IN_SEC,
            );
        },
        followToggle(withNotify = true) {
            this.$store.dispatch('document/toggleFollowDocument', this.document.id).then((val) => {
                if (val === undefined || !withNotify) return;

                notifySuccess(this.t(`document:show.${!val ? 'un' : ''}followSuccess`));
            });
        },
        studylistItemToggle() {
            this.$store.dispatch('document/toggleStudylistItem', this.document).then(() => {
                if (this.document?.study_list_id) {
                    this.$store.dispatch('flashMessages/showSuccess', {
                        content: this.t('document:show.addedToStudyListSuccess'),
                        progress: { time: 3000, showProgress: false },
                    });
                }
            });
        },
        async openEditPopup() {
            this.$modal.open(
                defineAsyncComponent(() => import('@components/ModalFormEditDocument')),
                {
                    documentId: this.document.id,
                    name: this.document.filename,
                    fileName: this.document.display_file_name,
                    contentTypeId: this.document.file_type,
                    semesterId: this.document.semester?.id,
                    description: this.document.description,
                    languageId: this.document.language?.id,
                    anonymous: this.document.user_data.anonymous,
                    selfMade: this.document.self_made,
                    professor: this.document.professor_name
                },
            );
        },
        reportDocument() {
            this.$modal.open(
                defineAsyncComponent(() => import('@components/ModalReportDocument')),
                { fileId: this.document.id },
            );
        },
        async deleteDocument() {
            await this.$modal.open(
                defineAsyncComponent(() => import('@/components/ModalFormDeleteDocument')),
                {
                    documentId: this.document.id,
                },
            );
        },
        stripWhiteSpaceSpecialCharacters,
        async joinUserToCourse() {
            const { course } = this.document;
            await this.$store.dispatch('course/join', {
                course,
                fromCoursePage: false,
            });
            this.pushGtmEvent(GtmEventName.JOIN_COURSE);

            this.$modal.open(
                defineAsyncComponent(() => import('./ModalCourseAutoJoined.vue')),
                {
                    course,
                },
            );
        },
    },
};
</script>
